const travelExpenseStates = [{
  value: 'DRAFT',
  label: 'Draft',
  phase: 'Pre-Travel',
  editable: true,
}, {
  value: 'SUBMITTED',
  label: 'Awaiting Approval',
  phase: 'Pre-Travel',
}, {
  value: 'REJECTED',
  label: 'Rejected',
  phase: 'Pre-Travel',
  editable: true,
}, {
  value: 'APPROVED',
  label: 'Awaiting Documentation',
  phase: 'Travel',
  editable: true,
}, {
  value: 'REIMBURSEMENT_REQUESTED',
  label: 'Awaiting Reimbursement Approval',
  phase: 'Post-Travel',
}, {
  value: 'REIMBURSEMENT_REJECTED',
  label: 'Reimbursement Rejected',
  phase: 'Post-Travel',
  editable: true,
}, {
  value: 'REIMBURSEMENT_APPROVED',
  label: 'Reimbursement Approved',
  phase: 'Post-Travel',
}, {
  value: 'PROCESSED',
  label: 'Reimbursement Processed',
  phase: 'Post-Travel',
}]

import moment from 'moment'

// GSA Privately Owned Vehicle (POV) mileage reimbursement rate, $/mile, when use of a POV is
// authorized. GSA publishes this as a static figure (no public JSON API), so update it here when
// the annual rate changes.
// Source: https://www.gsa.gov/travel/plan-a-trip/transportation-airfare-rates-pov-rates/privately-owned-vehicle-pov-mileage-reimbursement
// Effective 2026-01-01: $0.725/mile.
const POV_MILEAGE_RATE_PER_MILE = 0.725;

class TravelExpenseService {
  constructor($http, $rootScope) {
    "ngInject";
    this.$http = $http;
    this.expenseReportBaseUrl = $rootScope.apiHost + "/api/expense-reports";
    this.adminExpenseReportBaseUrl = $rootScope.apiHost + '/api/admin/expense-reports'

    this.dollarUS = Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    })
  }

  /**
   * Return the matching expense state
   *
   * @param {} status
   * @returns
   */
  getTravelExpenseStatusDetails(status) {
    return travelExpenseStates.find((expenseState) => expenseState.value === status)
  }

  submitForReimbursement(expense) {
      return this.updateExpenseReportStatus(expense.id, 'REIMBURSEMENT_REQUESTED')
  }

  updateExpenseReport(expense) {
    return this.$http.post(
      `${this.expenseReportBaseUrl}/${expense.id}`,
      this.serializeExpenseReport(expense),
      { withCredentials: true }
    )
  }

  updateExpenseReportStatus(expenseReportId, status, reason, processedDate, notify = true) {
    return this.$http.patch(`${this.adminExpenseReportBaseUrl}/${expenseReportId}/status`, {
      expenseReportStatus: status,
      reason,
      processedDate,
      notify,
    }, {withCredentials: true})
  }

  /**
   * Finalize a proxy import (moves it to Closed / PROCESSED).
   */
  finalizeProxyImport(expenseReportId, processedDate, notify = false) {
    return this.updateExpenseReportStatus(expenseReportId, 'PROCESSED', null, processedDate, notify);
  }

  /**
   * Reopen a PROCESSED proxy import for further editing.
   */
  resumeProxyImport(expenseReportId) {
    return this.$http.post(
      `${this.adminExpenseReportBaseUrl}/${expenseReportId}/resume-proxy-import`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * Proxy imports remain editable until explicitly finalized (PROCESSED).
   */
  isProxyImportEditable(expenseReport) {
    if (!expenseReport || !expenseReport.proxyCreatedByUserId) {
      return false;
    }
    return (expenseReport.status || '').toUpperCase() !== 'PROCESSED';
  }

  createExpenseReport(expense) {
    return this.$http.post(this.expenseReportBaseUrl, this.serializeExpenseReport(expense), {withCredentials: true})
  }

  getExpenseProjectOptionsForUser(userId) {
    return this.$http.get(
      `${this.adminExpenseReportBaseUrl}/project-options/${userId}`,
      { withCredentials: true }
    ).then((response) => this.normalizeExpenseProjectOptions(response.data));
  }

  normalizeExpenseProjectOptions(options) {
    return (options || []).map((option) => {
      let label = option.taskName || '';
      if (option.customerName) {
        label = option.customerName + ': ' + label;
      }
      return {
        taskId: option.taskId,
        taskName: option.taskName,
        label
      };
    });
  }

  /**
   * Create or update an expense report as a Draft, persisting wizardStep for resume.
   */
  saveDraftExpenseReport(expenseReport) {
    const payload = this.serializeExpenseReport(expenseReport);
    payload.status = 'DRAFT';

    if (expenseReport.id) {
      return this.$http.post(`${this.expenseReportBaseUrl}/${expenseReport.id}`, payload, { withCredentials: true });
    }

    return this.$http.post(this.expenseReportBaseUrl, payload, { withCredentials: true });
  }

  /**
   * Submit for approval. Creates or updates the report with status SUBMITTED.
   */
  submitExpenseReportForApproval(expenseReport) {
    const payload = this.serializeExpenseReport(expenseReport);
    payload.status = 'SUBMITTED';

    if (expenseReport.id) {
      return this.$http.post(`${this.expenseReportBaseUrl}/${expenseReport.id}`, payload, { withCredentials: true });
    }

    return this.$http.post(this.expenseReportBaseUrl, payload, { withCredentials: true });
  }

  serializeExpenseReport(expenseReport) {
    const payload = Object.assign({}, expenseReport);
    payload.expenses = (expenseReport.expenses || []).map((expense) => {
      const serializedExpense = Object.assign({}, expense);
      delete serializedExpense.estimateBasisFile;
      delete serializedExpense.estimateBasisFileName;
      delete serializedExpense.pendingReceiptIdsToDelete;
      return serializedExpense;
    });
    return payload;
  }

  /**
   * Resolve which wizard step to open when resuming a draft.
   */
  resolveWizardStep(expenseReport) {
    const savedStep = Number(expenseReport && expenseReport.wizardStep);
    if (savedStep >= 1 && savedStep <= 4) {
      return savedStep;
    }

    if (!expenseReport || !expenseReport.expenses) {
      return 1;
    }

    if (this.hasExpenses(expenseReport)) {
      return 3;
    }

    if (this.hasItinerary(expenseReport)) {
      return 2;
    }

    return 1;
  }

  normalizeExpenseReportDates(expenseReport) {
    if (!expenseReport) {
      return expenseReport;
    }

    if (expenseReport.processedDate) {
      expenseReport.processedDate = moment(expenseReport.processedDate).toDate();
    }

    if (!expenseReport.expenses) {
      return expenseReport;
    }

    expenseReport.expenses.forEach((expense) => {
      if (expense.fromDate) {
        expense.fromDate = moment(expense.fromDate).toDate();
      }
      if (expense.toDate) {
        expense.toDate = moment(expense.toDate).toDate();
      }
    });

    return expenseReport;
  }

  uploadExpenseReceipt(expenseReportId, expenseId, file) {
    const formData = new FormData();
    formData.append('file', file);

    return this.$http.post(
      `${this.expenseReportBaseUrl}/${expenseReportId}/expenses/${expenseId}/receipts`,
      formData,
      {
        headers: {
          'Content-Type': undefined
        },
        withCredentials: true,
        transformRequest: (data) => data
      }
    );
  }

  /**
   * Admin replace of a specific receipt (expense review). Works for any report status.
   */
  replaceExpenseReceipt(expenseReportId, expenseId, docId, file, receiptKind) {
    const formData = new FormData();
    formData.append('file', file);
    if (receiptKind) {
      formData.append('receiptKind', receiptKind);
    }

    return this.$http.post(
      `${this.adminExpenseReportBaseUrl}/${expenseReportId}/expenses/${expenseId}/receipts/${encodeURIComponent(docId)}/replace`,
      formData,
      {
        headers: {
          'Content-Type': undefined
        },
        withCredentials: true,
        transformRequest: (data) => data
      }
    );
  }

  /**
   * Admin upload when an expense line has no receipt (expense review). Works for any report status.
   */
  uploadExpenseReceiptAdmin(expenseReportId, expenseId, file, receiptKind) {
    const formData = new FormData();
    formData.append('file', file);
    if (receiptKind) {
      formData.append('receiptKind', receiptKind);
    }

    return this.$http.post(
      `${this.adminExpenseReportBaseUrl}/${expenseReportId}/expenses/${expenseId}/receipts`,
      formData,
      {
        headers: {
          'Content-Type': undefined
        },
        withCredentials: true,
        transformRequest: (data) => data
      }
    );
  }

  /**
   * Admin create of an expense line (e.g. itinerary location) for any report status.
   */
  createExpenseAdmin(expenseReportId, expensePayload) {
    return this.$http.post(
      `${this.adminExpenseReportBaseUrl}/${expenseReportId}/expenses`,
      expensePayload,
      { withCredentials: true }
    );
  }

  /**
   * Admin update of an expense line (e.g. itinerary location) for any report status.
   */
  updateExpenseAdmin(expenseReportId, expenseId, expensePayload) {
    return this.$http.patch(
      `${this.adminExpenseReportBaseUrl}/${expenseReportId}/expenses/${expenseId}`,
      expensePayload,
      { withCredentials: true }
    );
  }

  /**
   * Admin delete of an expense line (e.g. itinerary location) for any report status.
   */
  deleteExpenseAdmin(expenseReportId, expenseId) {
    return this.$http.delete(
      `${this.adminExpenseReportBaseUrl}/${expenseReportId}/expenses/${expenseId}`,
      { withCredentials: true }
    );
  }

  expensesMatchForUpload(localExpense, serverExpense) {
    if (!localExpense || !serverExpense || serverExpense.itinerary) {
      return false;
    }

    if (localExpense.id && serverExpense.id && String(localExpense.id) === String(serverExpense.id)) {
      return true;
    }

    if (localExpense.type !== serverExpense.type) {
      return false;
    }

    if ((localExpense.description || '').trim() !== (serverExpense.description || '').trim()) {
      return false;
    }

    if (localExpense.type === 'Personally Owned Vehicle') {
      return Number(localExpense.mileage || 0) === Number(serverExpense.mileage || 0);
    }

    return Number(localExpense.amount || 0) === Number(serverExpense.amount || 0);
  }

  findServerExpenseForUpload(localExpense, serverAdditional, usedIds) {
    if (localExpense.id) {
      const byId = serverAdditional.find((expense) =>
        String(expense.id) === String(localExpense.id) && !usedIds[expense.id]
      );
      if (byId) {
        return byId;
      }
    }

    return serverAdditional.find((expense) =>
      !usedIds[expense.id] && this.expensesMatchForUpload(localExpense, expense)
    );
  }

  getReceiptIds(expense) {
    return (expense && expense.receipts ? expense.receipts : [])
      .map((receipt) => receipt.receipt || receipt.id)
      .filter(Boolean);
  }

  /**
   * Delete receipts on an expense line before uploading replacement estimate-basis evidence.
   * Uses server receipts when present, otherwise any locally stashed pendingReceiptIdsToDelete.
   */
  removeExpenseReceipts(expenseReportId, serverExpense, localExpense) {
    const receiptIds = [];
    const seen = {};

    this.getReceiptIds(serverExpense).forEach((id) => {
      seen[id] = true;
      receiptIds.push(id);
    });

    ((localExpense && localExpense.pendingReceiptIdsToDelete) || []).forEach((id) => {
      if (!seen[id]) {
        seen[id] = true;
        receiptIds.push(id);
      }
    });

    if (!expenseReportId || !serverExpense || !serverExpense.id || !receiptIds.length) {
      return Promise.resolve(null);
    }

    return Promise.all(
      receiptIds.map((receiptId) => this.removeReceipt(expenseReportId, serverExpense.id, receiptId))
    );
  }

  /**
   * Upload local estimate-basis files to matching server expense lines, then return a refreshed report.
   * When the expense already has receipts, those are deleted first so only the new file remains.
   */
  uploadPendingEstimateBasisFiles(localReport, createdReport) {
    const localAdditional = (localReport.expenses || []).filter((expense) => !expense.itinerary && expense.estimateBasisFile);
    const serverAdditional = (createdReport.expenses || []).filter((expense) => !expense.itinerary);
    const usedIds = {};

    if (!localAdditional.length || !createdReport || !createdReport.id) {
      return Promise.resolve(createdReport);
    }

    const uploads = localAdditional.map((localExpense) => {
      const serverExpense = this.findServerExpenseForUpload(localExpense, serverAdditional, usedIds);

      if (!serverExpense || !serverExpense.id || !localExpense.estimateBasisFile) {
        return Promise.resolve(null);
      }

      usedIds[serverExpense.id] = true;

      return this.removeExpenseReceipts(createdReport.id, serverExpense, localExpense)
        .then(() => this.uploadExpenseReceipt(createdReport.id, serverExpense.id, localExpense.estimateBasisFile));
    });

    return Promise.all(uploads).then(() => this.getExpenseReport(createdReport.id).then((response) => response.data));
  }

  /**
   * Map persisted receipts onto estimateBasisFileName for wizard display.
   */
  hydrateEstimateBasisDisplay(expenseReport) {
    if (!expenseReport || !expenseReport.expenses) {
      return expenseReport;
    }

    expenseReport.expenses.forEach((expense) => {
      if (expense.itinerary) {
        return;
      }

      if (expense.estimateBasisFileName) {
        return;
      }

      if (expense.receipts && expense.receipts.length) {
        const receipt = expense.receipts[0];
        expense.estimateBasisFileName = receipt.documentName || receipt.receipt || 'Attached file';
      }
    });

    return expenseReport;
  }

  getEstimateBasisLabel(expense) {
    if (!expense) {
      return '';
    }

    if (expense.estimateBasisFileName) {
      return expense.estimateBasisFileName;
    }

    if (expense.receipts && expense.receipts.length) {
      const receipt = expense.receipts[0];
      return receipt.documentName || receipt.receipt || 'Attached file';
    }

    return '';
  }

  getExpenseReport(expenseReportId) {
    return this.$http.get(`${this.expenseReportBaseUrl}/${expenseReportId}`, {withCredentials: true})
  }

  deleteExpenseReport(expenseReportId) {
    return this.$http.delete(`${this.expenseReportBaseUrl}/${expenseReportId}`, {withCredentials: true})
  }

  removeExpense(expenseId) {
    return this.$http.delete(`${this.expenseReportBaseUrl}/expense/${expenseId}`, {withCredentials: true})
  }

  removeReceipt(expenseReportId, expenseId, receiptId) {
    return this.$http.delete(`${this.expenseReportBaseUrl}/${expenseReportId}/expenses/${expenseId}/receipts/${receiptId}`, {withCredentials: true})
  }

  getTotalForPhase(expenseReport, phase) {
    const actual =  phase !== 'Pre-Travel'
    return this.getTotalForExpenseReport(expenseReport, actual)
  }

  getActualTotalForExpenseReport(expenseReport) {
    return this.getTotalForExpenseReport(expenseReport, true)
  }

  getEstimatedTotalForExpenseReport(expenseReport) {
    return this.getTotalForExpenseReport(expenseReport, false)
  }

  isBillableTrip(expenseReport) {
    return expenseReport && expenseReport.purpose === 'customer';
  }

  isGeneralBusinessReport(expenseReport) {
    return !!expenseReport && expenseReport.reportType === 'GENERAL_BUSINESS';
  }

  isOtherDirectCostsReport(expenseReport) {
    return !!expenseReport && expenseReport.reportType === 'OTHER_DIRECT_COSTS';
  }

  /**
   * Fixed list of General Business purchase categories, mirroring the Purchase Request form.
   * "Other" allows the requester to enter a custom category label.
   */
  getGeneralBusinessCategories() {
    return [
      'Tuition Expense (College or Advanced Degree Program)',
      'Training Expense (Course, Certification Prep, Test Fee)',
      'Professional Organization Membership/Subscription',
      'Employee Recognition',
      'Office Supplies',
      'Marketing Materials',
      'Food/Snacks',
      'Other'
    ];
  }

  /**
   * Fixed list of Other Direct Cost categories for project billable expenses.
   * "Other" allows the requester to enter a custom category label.
   */
  getOtherDirectCostCategories() {
    return [
      'Materials & Supplies',
      'Equipment / Hardware',
      'Software / Licenses',
      'Professional / Consultant Services',
      'Shipping / Freight',
      'Printing / Reproduction',
      'Laboratory / Testing Services',
      'Facilities / Equipment Rental',
      'Other'
    ];
  }

  getTotalForExpenseReport(expenseReport, actual) {
    // let x = this.getTotalPerDiemForDaysTraveled(expenseReport);
    let y = actual ? this.getActualTotalTravelExpenses(expenseReport) : this.getEstimatedTotalTravelExpenses(expenseReport)
    return y;
  }

  getEstimatedTotalTravelExpenses(expenseReport) {
    let x = 0;
    expenseReport.expenses.forEach((expense) => {
      if(!expense.itinerary) {
        x += expense.amount || expense.actualAmount || 0
      } else if (this.isBillableTrip(expenseReport)) {
        x += this.getTotalPerDiem(expense);
        x += this.getTotalMeals(expense);
      }
    });
    return x;
  }

  getActualTotalTravelExpenses(expenseReport) {
    let x = 0;
    expenseReport.expenses.forEach((expense) => {
      if(!expense.itinerary) {
        x += expense.actualAmount || 0
      } else {
        x += expense.actualLodging || 0
        x += this.getTotalMeals(expense);
      }
    });

    return x;
  }

  /**
   * Amount payable directly to the employee: meal per diem plus actual
   * expenses paid personally. POV mileage is always treated as personal.
   */
  getEmployeeReimbursementTotal(expenseReport) {
    if (!expenseReport || !expenseReport.expenses) {
      return 0;
    }

    return expenseReport.expenses.reduce((total, expense) => {
      if (expense.itinerary) {
        const personalLodging = expense.paymentMethod === 'personal'
          ? (expense.actualLodging || 0)
          : 0;
        return total + this.getTotalMeals(expense) + personalLodging;
      }

      const isPersonal = expense.paymentMethod === 'personal'
        || expense.type === 'Personally Owned Vehicle';
      return total + (isPersonal ? (expense.actualAmount || 0) : 0);
    }, 0);
  }

  /**
   * Actual charges paid with the corporate card. These contribute to the
   * report's total cost but are excluded from employee reimbursement.
   */
  getCorporateCardTotal(expenseReport) {
    if (!expenseReport || !expenseReport.expenses) {
      return 0;
    }

    return expenseReport.expenses.reduce((total, expense) => {
      if (expense.paymentMethod !== 'corporate') {
        return total;
      }

      return total + (expense.itinerary
        ? (expense.actualLodging || 0)
        : (expense.actualAmount || 0));
    }, 0);
  }

  getTotalPerDiemForDaysTraveled(expenseReport){
    let x = 0;
    expenseReport.expenses.forEach((location) => {
      if (location.itinerary) {
        x += this.getTotalPerDiem(location);
      }
    });

    return x;
  }

  getTotalPerDiem(location) {
    let x = this.getTotalMeals(location) + this.getTotalLodging(location);
    return x;
  }

  getTotalMeals(location) {
    let fullTravelDays = location.daysTraveled - 2;

    let fullTravelDayMeals = location.mealsRate * fullTravelDays;

    let partialTravelDays = location.daysTraveled - fullTravelDays;
    let partialTravelMeals = (location.mealsRate * .75) * partialTravelDays;

    return partialTravelMeals + fullTravelDayMeals;
  }

  getTotalLodging(location) {
    let x = 0;
    if(location) {
      x = location.lodgingRate * location.daysTraveled;
    }

    return x;
  }

  getTotalLodgingCosts(expenseReport){
    let x = 0;
    expenseReport.expenses.forEach((location) => {
      if(location.itinerary) {
        x += this.getTotalLodging(location);
      }
    });

    return x;
  }

  getDatesOfTravel(expenseReport) {
    let fromDates = [];
    let toDates = [];
    expenseReport.expenses.forEach(function (expense){
      if(expense.itinerary) {
        fromDates.push(new Date(expense.fromDate));
        toDates.push(new Date(expense.toDate));
      }
    });

    if (!fromDates.length) {
      return '';
    }

    fromDates.sort((date1, date2) => date1 - date2);
    toDates.sort((date1, date2) => date1 - date2);

    return moment(fromDates[0]).format('MMM, DD, YYYY') +' - ' + moment(toDates[toDates.length -1]).format('MMM, DD, YYYY');
  }

  getTotalDaysTraveled(expenseReport){
    let x = 0;
    expenseReport.expenses.forEach((location) => {
      if(location.itinerary) {
        x += location.daysTraveled;
      }
    });

    return x;
  }

  getTotalMealsCosts(expenseReport){
    let x = 0;
    expenseReport.expenses.forEach((location) => {
      if(location.itinerary) {
        x += this.getTotalMeals(location);
      }
    });

    return x;
  }

  /**
   * Checks if the expense report has at least one itinerary.
   *
   * @param {} expenseReport
   * @returns
   */
  hasItinerary(expenseReport) {
    return expenseReport.expenses.some((e) => e.itinerary)
  }

  /**
   * Checks if the expense report has at least one non-itinerary item.
   *
   * @param {*} expenseReport
   * @returns
   */
  hasExpenses(expenseReport) {
    return expenseReport.expenses.some((e) => !e.itinerary)
  }

  getPovMileageRate() {
    return POV_MILEAGE_RATE_PER_MILE;
  }

  /**
   * Estimated reimbursement cost for a POV mileage expense, rounded to cents.
   */
  calculateMileageAmount(mileage) {
    const miles = parseFloat(mileage);

    if (isNaN(miles) || miles <= 0) {
      return 0;
    }

    return Math.round(miles * POV_MILEAGE_RATE_PER_MILE * 100) / 100;
  }

  isMileageExpense(expense) {
    return !!expense && expense.type === 'Personally Owned Vehicle';
  }

  /**
   * The backend persists `amount` but not `mileage`, so after a reload restore the mileage on
   * POV expense lines by deriving it from the saved cost and the current rate.
   */
  hydrateMileageDisplay(expenseReport) {
    if (!expenseReport || !expenseReport.expenses) {
      return expenseReport;
    }

    expenseReport.expenses.forEach((expense) => {
      if (!this.isMileageExpense(expense)) {
        return;
      }

      if ((expense.mileage == null || Number(expense.mileage) === 0) && expense.amount > 0) {
        expense.mileage = Math.round((expense.amount / POV_MILEAGE_RATE_PER_MILE) * 10) / 10;
      }
    });

    return expenseReport;
  }

  formatNumber(number) {
    return this.dollarUS.format(number)
  }

  formatDate(date) {
    return moment(date).format("MMM DD, YYYY")
  }

  formatDateTime(value) {
    if (!value) {
      return '';
    }
    return moment(value).format('MMM DD, YYYY h:mm A');
  }

  getStatusLabel(status) {
    const details = this.getTravelExpenseStatusDetails(status);
    return details ? details.label : (status || '');
  }

  getStatusHistory(expenseReport) {
    return (expenseReport && expenseReport.statusHistory) ? expenseReport.statusHistory : [];
  }

  getStatusHistoryActorName(entry) {
    if (!entry) {
      return '';
    }
    return ((entry.actorFirstName || '') + ' ' + (entry.actorLastName || '')).trim() || 'Unknown';
  }

  getStatusHistoryActionLabel(entry) {
    if (!entry || !entry.toStatus) {
      return '';
    }

    switch ((entry.toStatus || '').toUpperCase()) {
      case 'SUBMITTED':
        return 'Submitted for approval';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      case 'REIMBURSEMENT_REQUESTED':
        return 'Submitted for reimbursement';
      case 'REIMBURSEMENT_APPROVED':
        return 'Reimbursement approved';
      case 'REIMBURSEMENT_REJECTED':
      case 'REIMBURSMENT_REJECTED':
        return 'Reimbursement rejected';
      case 'PROCESSED':
        return 'Marked as processed';
      case 'DRAFT':
        return 'Saved as draft';
      default:
        return this.getStatusLabel(entry.toStatus);
    }
  }

}

export default TravelExpenseService



// WEBPACK FOOTER //
// ./client/common/services/travel-expenses/travel-expenses.service.js